import { CARD_MIN_WIDTH, CARD_MIN_WIDTH_SMALL } from '../../settings/integrations/integrations.styles'
import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const SSMCardContainer = styled(Box)<{ isloading?: string }>`
  padding: ${theme.spacing.small}px;
  border: 1px solid ${theme.colors.strokePrimary};
  background: ${theme.colors.borderMedium};
  position: relative;
  display: flex;

  html[data-theme='light'] & {
    border-color: ${theme.colors.dropdownBorder};
    background: ${theme.colors.pageBackground};

    &.with-items {
      background: ${theme.colors.surfaceContainerBackground};
    }

    &:hover {
      background: ${theme.colors.legacySurface502};
    }
  }

  .edit-icon-button {
    position: absolute;
    top: 0;
    right: 0.4px;
    border-radius: 0 0 0 ${theme.attributes.borderRadius.secondary};
    background: ${theme.colors.cta};
    display: flex;
    width: 32px;
    height: 32px;
    justify-content: center;
    align-items: center;
    padding: 0;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    html[data-theme='light'] & {
      /* CSS fill beats the glyph's own presentation attribute, which can't take a var() */
      svg path {
        fill: ${theme.colors.menuItemHover};
      }
    }
  }

  &:hover {
    .edit-icon-button {
      opacity: 1;
    }
  }

  &.with-items {
    background: ${theme.colors.surfaceContainerBackground};
  }

  &:hover {
    border: 1px solid ${theme.colors.controlBorder};
    background: ${theme.colors.legacySurface502};
  }

  ${({ isloading }) =>
    isloading &&
    isloading === 'true' &&
    `
      border: 1px solid ${theme.colors.surfaceContainerBackground};
      background:${theme.colors.legacySurfaceItemBackground};
  `};
`

export const CardItem = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.half}px;
  width: 100%;

  .MuiTypography-root {
    overflow: hidden;
    color: ${theme.colors.foreground};
    text-align: center;
    text-overflow: ellipsis;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: normal;
    white-space: nowrap;
  }

  .missing-logo-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #c3c3c3;
    border-radius: ${theme.attributes.borderRadius.circle};
    color: ${theme.baseColors.greyscale[0]};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: normal;
    text-align: center;
    text-transform: capitalize;
  }
`

export const ProductCount = styled(Box)`
  color: ${theme.colors.softMauve};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const IconsRow = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: fit-content;

  > :last-child {
    margin-right: 0;
  }
`

export const IconWrapper = styled(Box)`
  flex-shrink: 0;
  margin-right: -16px;

  &:last-of-type {
    margin-right: 0;
  }

  @media screen and (max-width: 1200px) {
    margin-right: -8px;
  }
`

export const OverflowIndicator = styled(Box)`
  flex-shrink: 0;
  margin-right: -16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.attributes.borderRadius.circle};
  background: ${theme.colors.legacyPrimaryLight3};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.3), 0px 8px 12px 0px rgba(0, 0, 0, 0.15);
  overflow: clip;

  html[data-theme='light'] & {
    box-shadow: none;
  }

  color: ${theme.colors.dustyRoseGray};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 20px;
  letter-spacing: 0.1px;
  text-align: center;
  cursor: default;
  z-index: 1;

  @media screen and (max-width: 1200px) {
    width: 32px;
    height: 32px;
    margin-right: -8px;
  }
`

export const HoverTooltipContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 4px;
    background: transparent;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.cta};
    border-radius: ${theme.attributes.borderRadius.quaternary};
    html[data-theme='light'] & {
      background-color: ${theme.colors.borderMedium};
    }
  }

  .hover-tooltip-item {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.small}px;

    .hover-tooltip-logo {
      width: 36px;
      height: 36px;
      border-radius: ${theme.attributes.borderRadius.circle};
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;
      flex-shrink: 0;
    }

    .hover-tooltip-missing-logo {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: ${theme.attributes.borderRadius.circle};
      background: ${theme.colors.avatarPlaceholderBg};
      flex-shrink: 0;
      color: ${theme.baseColors.greyscale[0]};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-weight: ${theme.typography.fontWeight.normal};
      text-transform: capitalize;
    }

    .hover-tooltip-text {
      display: flex;
      flex-direction: column;

      .hover-tooltip-product-name {
        color: ${theme.colors.menuItemText};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.medium};
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 16px;
        letter-spacing: 0.5px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .hover-tooltip-vendor-name {
        color: ${theme.colors.calendarDay};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.medium};
        font-weight: ${theme.typography.fontWeight.normal};
        line-height: 20px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`

export const IntegrationLogo = styled(Box)<{ logo: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${theme.attributes.borderRadius.circle};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.3), 0px 8px 12px 0px rgba(0, 0, 0, 0.15);
  background: ${({ logo }) => `url("${logo}") no-repeat center`};
  background-size: cover;

  html[data-theme='light'] & {
    box-shadow: none;
  }

  @media screen and (max-width: 1200px) {
    width: 32px;
    height: 32px;
  }
`

export const IntegrationItemWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const NoIntegrationLogo = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: ${theme.attributes.borderRadius.circle};
  background: ${theme.colors.avatarPlaceholderBg};

  @media screen and (max-width: 1200px) {
    width: 32px;
    height: 32px;
  }
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.3), 0px 8px 12px 0px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;

  html[data-theme='light'] & {
    box-shadow: none;
  }

  .no-integration-logo-letter {
    color: #342f32;
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.mediumSmall}px;
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
  }
`

export const SSMCardInner = styled(Box)<{ isediting?: string; isCurrentEdit?: boolean }>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ isCurrentEdit }) =>
    isCurrentEdit &&
    `
     background: ${theme.colors.legacyPrimaryLight3};
    `};

  ${({ isediting }) =>
    isediting &&
    isediting === 'true' &&
    `
      &:hover {
        border-radius: 2px;
        border: 1px solid ${theme.colors.betaChipInactive};
        background: ${theme.colors.legacyPrimaryLight3};
        box-shadow: ${theme.attributes.boxShadow.chipCircle};
        cursor: pointer;
      }
    `};

  .text {
    color: ${theme.colors.menuItemText};
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.mediumSmall};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
  }
`

export const SSMCardSkeletonWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  width: 100%;
`

export const SSMCardSkeleton = styled(Box)`
  width: 100%;
  height: 100%;
  flex-grow: 1;

  &.integrations {
    min-width: ${CARD_MIN_WIDTH}px;
    color: ${theme.colors.foreground};
    transition: transform 0.2s;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    border: 1px solid ${theme.colors.borderMedium};
    background: ${theme.colors.surfaceContainerBackground};
    height: 240px;

    .card-skeleton-inner {
      border-radius: ${theme.attributes.borderRadius.tertiary};
    }

    @media screen and (min-width: 1200px) and (max-width: 1920px) {
      min-width: ${CARD_MIN_WIDTH_SMALL}px;
    }
  }

  .card-skeleton-inner {
    width: 100%;
    height: 100%;
    background-color: ${theme.colors.legacyPrimaryLight3};

    &:after {
      background: ${theme.colors.ssmCardSkeleton};
    }

    html[data-theme='light'] & {
      background-color: ${theme.colors.borderMedium};
    }
  }
`
